import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { User, ChevronLeft, ChevronRight, Newspaper } from "lucide-react";
import { FaLinkedin } from "react-icons/fa";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { useState } from "react";
import { getArticleUrl } from "@/lib/article-url";

const PAGE_SIZE = 12;

interface PublicPerson {
  id: string;
  firstName: string;
  lastName: string;
  slug: string;
  avatar: string | null;
  jobTitle: string | null;
  bio: string | null;
  linkedin: string | null;
}

interface PublicArticleSnippet {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featuredImage: string | null;
  publishedAt: string | null;
  createdAt: string;
}

interface PersonArticlesResponse {
  person: PublicPerson;
  articles: PublicArticleSnippet[];
  total: number;
  page: number;
  totalPages: number;
}

export default function PeopleHubPage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useQuery<PersonArticlesResponse>({
    queryKey: [`/api/people/${slug}/articles?page=${page}&limit=${PAGE_SIZE}`],
    enabled: !!slug,
  });

  const person = data?.person;
  const articles = data?.articles ?? [];
  const totalPages = data?.totalPages ?? 1;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="max-w-7xl mx-auto px-4 pt-4 w-full">
          <PageBreadcrumb items={[{ label: "People" }, { label: "Loading…" }]} />
        </div>
        <div className="flex-1 max-w-7xl mx-auto px-4 py-10 w-full">
          <Skeleton className="h-10 w-64 mb-2" />
          <Skeleton className="h-5 w-96 mb-10" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-56 rounded-lg" />
            ))}
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (!person) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <User className="h-12 w-12 text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold mb-2">Person Not Found</h1>
          <p className="text-muted-foreground mb-6">This person page doesn't exist.</p>
          <Button asChild><Link href="/">Go Home</Link></Button>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader />
      <div className="max-w-7xl mx-auto px-4 pt-4 w-full">
        <PageBreadcrumb items={[{ label: "People" }, { label: `${person.firstName} ${person.lastName}` }]} />
      </div>
      <div className="flex-1 max-w-7xl mx-auto px-4 pb-8 lg:pb-12 w-full">
        <div className="flex items-start gap-6 mb-10 pb-8 border-b">
          <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
            {person.avatar ? (
              <img src={person.avatar} alt={`${person.firstName} ${person.lastName}`} className="w-full h-full object-cover" />
            ) : (
              <User className="h-10 w-10 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap mb-1">
              <h1 className="text-3xl font-bold" data-testid="text-person-name">{person.firstName} {person.lastName}</h1>
              {person.linkedin && (
                <a
                  href={person.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors"
                  data-testid="link-person-linkedin"
                >
                  <FaLinkedin className="h-5 w-5" />
                </a>
              )}
            </div>
            {person.jobTitle && (
              <p className="text-sm font-medium text-primary mb-1" data-testid="text-person-jobtitle">{person.jobTitle}</p>
            )}
            {person.bio && (
              <p className="text-muted-foreground text-base leading-relaxed" data-testid="text-person-bio">{person.bio}</p>
            )}
            <p className="text-sm text-muted-foreground mt-2">
              {data?.total ?? 0} article{(data?.total ?? 0) !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        {articles.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Newspaper className="h-12 w-12 mx-auto mb-4 opacity-40" />
            <p className="text-lg">No published articles featuring this person yet.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {articles.map((article) => (
                <Link key={article.id} href={getArticleUrl(article)} data-testid={`link-article-${article.id}`}>
                  <Card className="group hover-elevate h-full overflow-visible flex flex-col cursor-pointer">
                    {article.featuredImage && (
                      <div className="aspect-[16/10] w-full overflow-hidden rounded-t-md bg-muted">
                        <img
                          src={article.featuredImage}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      </div>
                    )}
                    <CardContent className="pt-4 pb-4 flex-1 flex flex-col">
                      <h2 className="font-semibold text-base line-clamp-2 group-hover:text-primary transition-colors mb-2" data-testid={`text-article-title-${article.id}`}>
                        {article.title}
                      </h2>
                      {article.excerpt && (
                        <p className="text-sm text-muted-foreground line-clamp-2 flex-1">{article.excerpt}</p>
                      )}
                      {article.publishedAt && (
                        <p className="text-xs text-muted-foreground mt-3">
                          {new Date(article.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2" data-testid="pagination-controls">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="button-prev-page"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground px-2">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  data-testid="button-next-page"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
